import { IGenericBarItem } from '../types';

export interface IBarChartDimensions {
  svgWidth: number;
  svgHeight: number;
  padLeft: number;
  padRight: number;
  padTop: number;
  padBottom: number;
  gap: number;
  tickCount: number;
}

export interface IBarChartBar {
  x: number;
  y: number;
  width: number;
  height: number;
  rawHeight: number;
  isPositive: boolean;
}

export interface IBarChartLayout {
  chartW: number;
  chartH: number;
  toY: (value: number) => number;
  zeroY: number;
  ticks: number[];
  barWidth: number;
  bars: IBarChartBar[];
  yearFooter: string;
}

export function computeBarChartLayout(
  items: IGenericBarItem[],
  dims: IBarChartDimensions
): IBarChartLayout {
  const {
    svgWidth,
    padLeft,
    padRight,
    padTop,
    padBottom,
    svgHeight,
    gap,
    tickCount,
  } = dims;

  const chartW = svgWidth - padLeft - padRight;
  const chartH = svgHeight - padTop - padBottom;

  const vals = items.map((d) => d.value);
  const dataMin = Math.min(0, ...vals);
  const dataMax = Math.max(0, ...vals);
  const range = dataMax - dataMin || 1;
  const pad = Math.max(5, range * 0.12);
  const yMin = dataMin - pad;
  const yMax = dataMax + pad;
  const yRange = yMax - yMin;

  const toY = (v: number) => padTop + ((yMax - v) / yRange) * chartH;
  const zeroY = toY(0);

  const n = items.length;
  const barWidth = Math.max(4, (chartW - gap * (n + 1)) / n);

  const years = [
    ...new Set(
      items
        .map((d) => {
          const m = d.label.match(/\d{4}/);
          return m ? m[0] : '';
        })
        .filter(Boolean)
    ),
  ];
  const yearFooter = years.join(' – ');

  const tickStep = yRange / tickCount;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) =>
    parseFloat((yMin + i * tickStep).toFixed(1))
  );

  const bars: IBarChartBar[] = items.map((item, i) => {
    const x = padLeft + gap + i * (barWidth + gap);
    const isPositive = item.value >= 0;
    const rawHeight = Math.abs(toY(item.value) - zeroY);
    const height = Math.max(2, rawHeight);
    const y = isPositive ? zeroY - rawHeight : zeroY;
    return { x, y, width: barWidth, height, rawHeight, isPositive };
  });

  return { chartW, chartH, toY, zeroY, ticks, barWidth, bars, yearFooter };
}
